import React, { forwardRef, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import './tableTextInput.css';
import { useApiData } from '../../../contexts/apiService';

interface TableTextInputProps {
    tableName: string;
    rowId: string | number;
    columnName: string;
    value: string | number | undefined;
    parentId?: number;
    placeholder?: string;
    label?: string;
    width?: number;
    height?: number;
    disabled?: boolean;
    onSuccess?: () => void;
    onError?: () => void;
    validate?: (value: string) => string | null;
}

const TableTextInput = forwardRef<HTMLInputElement, TableTextInputProps>(({
    tableName,
    rowId,
    columnName,
    value,
    parentId,
    placeholder,
    label,
    width,
    height = 30,
    disabled = false,
    onSuccess,
    onError,
    validate,
}, ref) => {
    
    const { handleEditOrder } = useApiData();

    const [inputValue, setInputValue] = useState<string>(value !== undefined && value !== null ? String(value) : '');
    const [isLoading, setIsLoading] = useState(false);
    const [flashSuccess, setFlashSuccess] = useState(false);
    const [flashError, setFlashError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Sync when value prop changes (e.g. panel opens with a different row)
    useEffect(() => {
        setInputValue(value !== undefined && value !== null ? String(value) : '');
        setErrorMessage(null);
    }, [value]);
    
    const handleSubmit = async () => {
        const oldValue = value !== undefined && value !== null ? String(value) : '';

        // No change — do nothing
        if (inputValue === oldValue) return;

        // Run validation if provided
        if (validate) {
            const validationError = validate(inputValue);
            if (validationError) {
                setErrorMessage(validationError);
                return;
            }
        }

        setErrorMessage(null);
        setIsLoading(true);

        const [success] = await handleEditOrder(tableName, rowId, columnName, oldValue, inputValue, parentId);

        setIsLoading(false);

        if (success) {
            setFlashSuccess(true);
            setTimeout(() => setFlashSuccess(false), 1000);
            onSuccess?.();
        } else {
            setInputValue(oldValue); // Reset to original
            setFlashError(true);
            setTimeout(() => setFlashError(false), 1000);
            onError?.();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSubmit();
        } else if (e.key === 'Escape') {
            const oldValue = value !== undefined && value !== null ? String(value) : '';
            setInputValue(oldValue);
            setErrorMessage(null);
        }
    };

    return (
        <div
            className={`editTextInput-container${flashSuccess ? ' editTextInput-container--success' : ''}${flashError ? ' editTextInput-container--error' : ''}`}
            style={{ width: width ? `${width}px` : '100%', position: 'relative', display: 'inline-block' }}
        >
            {label && <label>{label}</label>}
            <input
                value={inputValue}
                onChange={(e) => {
                    setInputValue(e.target.value);
                    setErrorMessage(null);
                }}
                onKeyDown={handleKeyDown}
                onBlur={handleSubmit}
                placeholder={placeholder}
                disabled={disabled || isLoading}
                className={`editTextInput-input${errorMessage ? ' editTextInput-input--error' : ''}`}
                style={{ height: `${height}px` }}
            />
            {errorMessage && !isLoading && (
                <div className="editTextInput-icon" title={errorMessage}>
                    <AlertCircle size={14} style={{ color: 'var(--color-warning-red)' }} />
                </div>
            )}
        </div>
    );
});

export default TableTextInput;